import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarClock } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { tripsApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { formatDate } from '@/utils/format';
import type {
  TransportAttendanceStatus,
  TransportTrip,
  TripGenerationResult,
  TripRider,
} from '@/types/transport';
import {
  AttendanceStatusBadge,
  Badge,
  Blockers,
  Stat,
  TripStatusBadge,
  delayText,
  formatTime,
  humanise,
  today,
} from './shared';

/**
 * The marks a finger can reach from a bus step. Three, not five: BOARDED and
 * ABSENT are the morning's questions, DROPPED_OFF is the afternoon's. The
 * rarer NOT_BOARDED and EXCUSED are follow-up states the office records from
 * the trip screen, not something to squeeze onto a phone.
 */
const MARKS: { value: TransportAttendanceStatus; label: string; tone: string }[] = [
  { value: 'BOARDED', label: 'Boarded', tone: 'bg-success/10 text-success border-success/30' },
  { value: 'ABSENT', label: 'Absent', tone: 'bg-warning/10 text-warning border-warning/30' },
  { value: 'DROPPED_OFF', label: 'Dropped off', tone: 'bg-info/10 text-info border-info/30' },
];

type LifecycleAction = 'start' | 'depart' | 'complete' | 'delay' | 'cancel';

/**
 * The day board — §22's daily-operations view.
 *
 * <p>Every trip for the chosen day in start order, and one tap from any of them
 * to its manifest. Built for a phone held in one hand at the school gate: the
 * board is a column of cards, the roster is a list of tall tap targets, and a
 * single tap posts a single mark — the AttendancePage batch pattern would be
 * wrong here, because a bus aisle has no "save register" moment.
 *
 * <p>Nothing on this screen is computed locally. The board's counts summarise
 * the rows the server sent, the roster's expected/boarded/unmarked figures are
 * the server's, and a generation run's skipped routes are shown in the server's
 * own words.
 */
export function DailyOperationsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const manages = can(PERMISSIONS.TRANSPORT_TRIP_MANAGE);
  const seesRoster = can(PERMISSIONS.TRANSPORT_ATTENDANCE_VIEW);
  const records = can(PERMISSIONS.TRANSPORT_ATTENDANCE_RECORD);

  const [date, setDate] = useState(today());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [generation, setGeneration] = useState<TripGenerationResult | null>(null);
  const [delaying, setDelaying] = useState(false);
  const [delayMinutes, setDelayMinutes] = useState('');
  const [delayReason, setDelayReason] = useState('');
  const [markingId, setMarkingId] = useState<string | null>(null);

  const board = useQuery({
    queryKey: queryKeys.trips.board(date),
    queryFn: () => tripsApi.board(date),
  });

  const selected = board.data?.find((trip) => trip.id === selectedId);

  const roster = useQuery({
    queryKey: queryKeys.trips.roster(selectedId ?? ''),
    queryFn: () => tripsApi.roster(selectedId ?? ''),
    enabled: selectedId !== null && seesRoster,
  });

  const invalidateTrips = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.trips.all });
  };

  const generate = useMutation({
    mutationFn: () => tripsApi.generate(date),
    onSuccess: (result) => {
      setGeneration(result);
      toast.success(
        `${result.created} created, ${result.alreadyScheduled} already scheduled` +
          (result.skipped.length > 0 ? `, ${result.skipped.length} skipped` : ''),
      );
      invalidateTrips();
    },
    onError: (error) => handleApiError(error),
  });

  const lifecycle = useMutation({
    mutationFn: (input: { id: string; action: LifecycleAction; minutes?: number; reason?: string }) => {
      if (input.action === 'start') {
        return tripsApi.start(input.id);
      }
      if (input.action === 'depart') {
        return tripsApi.depart(input.id);
      }
      if (input.action === 'complete') {
        return tripsApi.complete(input.id);
      }
      if (input.action === 'delay') {
        return tripsApi.delay(input.id, input.minutes ?? 0, input.reason);
      }
      return tripsApi.cancel(input.id, input.reason ?? '');
    },
    onSuccess: (trip) => {
      toast.success(`${trip.tripNumber} is now ${humanise(trip.status)}`);
      setDelaying(false);
      setDelayMinutes('');
      setDelayReason('');
      invalidateTrips();
    },
    onError: (error) => handleApiError(error),
  });

  /* A single tap is a list of one; the response is the fresh roster, which
     replaces the cache directly rather than waiting for a refetch. */
  const mark = useMutation({
    mutationFn: (input: { tripId: string; enrollmentId: string; status: TransportAttendanceStatus }) =>
      tripsApi.mark(input.tripId, {
        marks: [{ enrollmentId: input.enrollmentId, status: input.status }],
      }),
    onMutate: (input) => setMarkingId(input.enrollmentId),
    onSuccess: (fresh, input) => {
      queryClient.setQueryData(queryKeys.trips.roster(input.tripId), fresh);
    },
    onError: (error) => handleApiError(error),
    onSettled: () => setMarkingId(null),
  });

  const cancelTrip = (trip: TransportTrip) => {
    const why = window.prompt(
      "Cancelling tells every rider's guardian the bus is not coming. Why?",
    );
    if (why !== null && why.trim() !== '') {
      lifecycle.mutate({ id: trip.id, action: 'cancel', reason: why.trim() });
    }
  };

  const acting = (action: LifecycleAction) =>
    lifecycle.isPending && lifecycle.variables?.action === action;

  // ------------------------------------------------------------ roster view

  if (selected !== undefined) {
    const live =
      selected.status === 'SCHEDULED' ||
      selected.status === 'BOARDING' ||
      selected.status === 'IN_PROGRESS' ||
      selected.status === 'DELAYED';

    return (
      <div>
        <PageHeader
          title={selected.routeName}
          description={`${selected.tripNumber} · ${humanise(selected.direction)} · ${formatDate(selected.tripDate)}`}
          icon={<CalendarClock className="h-5 w-5" aria-hidden="true" />}
          action={
            <Button variant="secondary" onClick={() => setSelectedId(null)}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to the board
            </Button>
          }
        />

        <Card className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <TripStatusBadge status={selected.status} />
              {selected.status === 'DELAYED' && (
                <span className="text-xs text-[color:var(--sms-warning)]">
                  {delayText(selected.delayMinutes)}
                  {selected.delayReason !== undefined ? ` — ${selected.delayReason}` : ''}
                </span>
              )}
              <span className="text-sm text-muted">
                planned {formatTime(selected.plannedStartTime)} ·{' '}
                {selected.vehicleName ?? 'no vehicle'} · {selected.driverName ?? 'no driver'}
              </span>
            </div>

            {manages && (
              <div className="flex flex-wrap gap-2">
                {selected.status === 'SCHEDULED' && (
                  <Button
                    size="sm"
                    loading={acting('start')}
                    onClick={() => lifecycle.mutate({ id: selected.id, action: 'start' })}
                  >
                    Open boarding
                  </Button>
                )}
                {(selected.status === 'BOARDING' || selected.status === 'DELAYED') && (
                  <Button
                    size="sm"
                    loading={acting('depart')}
                    onClick={() => lifecycle.mutate({ id: selected.id, action: 'depart' })}
                  >
                    Depart
                  </Button>
                )}
                {(selected.status === 'IN_PROGRESS' || selected.status === 'DELAYED') && (
                  <Button
                    size="sm"
                    loading={acting('complete')}
                    onClick={() => lifecycle.mutate({ id: selected.id, action: 'complete' })}
                  >
                    Complete
                  </Button>
                )}
                {live && (
                  <Button size="sm" variant="secondary" onClick={() => setDelaying(true)}>
                    Delay…
                  </Button>
                )}
                {live && selected.status !== 'IN_PROGRESS' && (
                  <Button size="sm" variant="ghost" onClick={() => cancelTrip(selected)}>
                    Cancel trip
                  </Button>
                )}
              </div>
            )}
          </div>
          {selected.status === 'CANCELLED' && selected.cancellationReason !== undefined && (
            <p className="mt-2 text-xs text-[color:var(--sms-danger)]">
              Cancelled: {selected.cancellationReason}
            </p>
          )}
        </Card>

        {!seesRoster ? (
          <Card className="mt-4 p-6 text-center text-sm text-muted">
            You do not have permission to see the manifest.
          </Card>
        ) : roster.isPending ? (
          <LoadingState label="Loading the manifest" />
        ) : roster.error !== null ? (
          <ErrorState error={roster.error} onRetry={() => void roster.refetch()} />
        ) : roster.data === undefined ? null : (
          <>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <Stat label="Expected" value={roster.data.expected} />
              <Stat
                label="Boarded"
                value={roster.data.boarded}
                tone={roster.data.boarded > 0 ? 'success' : 'neutral'}
              />
              <Stat
                label="Unmarked"
                value={roster.data.unmarked}
                tone={roster.data.unmarked > 0 ? 'warning' : 'neutral'}
              />
            </div>

            <Card className="mt-4 p-4">
              {roster.data.riders.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">
                  Nobody is enrolled to ride this trip.
                </p>
              ) : (
                <ul className="divide-y divide-line">
                  {roster.data.riders.map((rider) => (
                    <RiderRow
                      key={rider.enrollmentId}
                      rider={rider}
                      canMark={records}
                      busy={mark.isPending && markingId === rider.enrollmentId}
                      onMark={(status) =>
                        mark.mutate({
                          tripId: selected.id,
                          enrollmentId: rider.enrollmentId,
                          status,
                        })
                      }
                    />
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}

        <Modal
          open={delaying}
          onClose={() => setDelaying(false)}
          title="Record a delay"
          description="When the school has delay notices on, every rider's guardian is told."
          footer={
            <>
              <Button variant="secondary" onClick={() => setDelaying(false)}>
                Cancel
              </Button>
              <Button
                loading={acting('delay')}
                disabled={!(Number(delayMinutes) > 0)}
                onClick={() =>
                  lifecycle.mutate({
                    id: selected.id,
                    action: 'delay',
                    minutes: Number(delayMinutes),
                    reason: delayReason.trim() === '' ? undefined : delayReason.trim(),
                  })
                }
              >
                Record it
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <TextField
              label="Minutes late"
              type="number"
              min={1}
              required
              value={delayMinutes}
              onChange={(event) => setDelayMinutes(event.target.value)}
            />
            <TextField
              label="Reason"
              value={delayReason}
              onChange={(event) => setDelayReason(event.target.value)}
              hint="Guardians read this — 'traffic on the bypass' beats silence."
            />
          </div>
        </Modal>
      </div>
    );
  }

  // ------------------------------------------------------------- board view

  const trips = board.data ?? [];
  const running = trips.filter(
    (trip) => trip.status === 'BOARDING' || trip.status === 'IN_PROGRESS',
  ).length;
  const delayed = trips.filter((trip) => trip.status === 'DELAYED').length;
  const completed = trips.filter((trip) => trip.status === 'COMPLETED').length;
  const cancelled = trips.filter((trip) => trip.status === 'CANCELLED').length;

  return (
    <div>
      <PageHeader
        title="Daily operations"
        description="Every trip for the day in start order. Tap a trip to open its manifest."
        icon={<CalendarClock className="h-5 w-5" aria-hidden="true" />}
        action={
          manages ? (
            <Button loading={generate.isPending} onClick={() => generate.mutate()}>
              Generate the day's trips
            </Button>
          ) : undefined
        }
      />

      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="w-full max-w-[180px]">
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
                setGeneration(null);
                setSelectedId(null);
              }}
            />
          </div>
          {trips.length > 0 && (
            <p className="text-sm text-muted">
              {trips.length} trips
              {running > 0 && <span className="ml-2">{running} running</span>}
              {delayed > 0 && (
                <span className="ml-2 text-[color:var(--sms-warning)]">{delayed} delayed</span>
              )}
              {completed > 0 && <span className="ml-2">{completed} completed</span>}
              {cancelled > 0 && (
                <span className="ml-2 text-[color:var(--sms-danger)]">{cancelled} cancelled</span>
              )}
            </p>
          )}
        </div>
      </Card>

      {generation !== null && (
        <Card className="mt-4 p-4">
          <p className="text-sm text-ink">
            Generation for {formatDate(generation.date)}:{' '}
            <span className="font-semibold">{generation.created} created</span>,{' '}
            {generation.alreadyScheduled} already scheduled.
          </p>
          {generation.skipped.length > 0 && (
            <div className="mt-3">
              {/* The server's reasons, verbatim — a silent skip is a bus that
                  silently never came. */}
              <Blockers reasons={generation.skipped} title="Routes that could not be filled" />
            </div>
          )}
        </Card>
      )}

      <div className="mt-4">
        {board.isPending ? (
          <LoadingState label="Loading the board" />
        ) : board.error !== null ? (
          <ErrorState error={board.error} onRetry={() => void board.refetch()} />
        ) : trips.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted">
            No trips are scheduled for this day.
            {manages && ' Generate the day\'s trips to fill the board from the active routes.'}
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {trips.map((trip) => (
              <button
                key={trip.id}
                type="button"
                onClick={() => setSelectedId(trip.id)}
                className="w-full text-left"
              >
                <Card interactive className="h-full p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-ink">{trip.routeName}</p>
                      <p className="font-mono text-[11px] text-subtle">{trip.tripNumber}</p>
                    </div>
                    <TripStatusBadge status={trip.status} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted">
                    <Badge tone={trip.direction === 'PICKUP' ? 'info' : 'neutral'}>
                      {humanise(trip.direction)}
                    </Badge>
                    <span className="tabular-nums">{formatTime(trip.plannedStartTime)}</span>
                    <span className="min-w-0 truncate">
                      {trip.vehicleName ?? 'no vehicle'} · {trip.driverName ?? 'no driver'}
                    </span>
                  </div>
                  {trip.status === 'DELAYED' && (
                    <p className="mt-2 text-xs text-[color:var(--sms-warning)]">
                      {delayText(trip.delayMinutes)}
                      {trip.delayReason !== undefined ? ` — ${trip.delayReason}` : ''}
                    </p>
                  )}
                  {trip.status === 'CANCELLED' && trip.cancellationReason !== undefined && (
                    <p className="mt-2 text-xs text-[color:var(--sms-danger)]">
                      {trip.cancellationReason}
                    </p>
                  )}
                </Card>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One rider, phone-first: the identity on top, the server's mark beside it,
 * and three tall tap targets underneath. The buttons show intent; the badge
 * shows what the server actually recorded.
 */
function RiderRow({
  rider,
  canMark,
  busy,
  onMark,
}: {
  rider: TripRider;
  canMark: boolean;
  busy: boolean;
  onMark: (status: TransportAttendanceStatus) => void;
}) {
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">
            {rider.studentName}
            {rider.guest && (
              <span className="ml-2 align-middle">
                {/* A ride the enrollment does not name — always flagged. */}
                <Badge tone="warning">guest</Badge>
              </span>
            )}
          </p>
          <p className="truncate text-[11px] text-subtle">
            {rider.studentNumber !== undefined && (
              <span className="font-mono">{rider.studentNumber} · </span>
            )}
            {rider.stopName ?? 'no stop'}
            {rider.guardianPhone !== undefined && (
              <>
                {' · '}
                <a className="text-accent" href={`tel:${rider.guardianPhone}`}>
                  {rider.guardianPhone}
                </a>
              </>
            )}
          </p>
          {rider.specialInstructions !== undefined && (
            <p className="mt-0.5 text-[11px] text-[color:var(--sms-warning)]">
              {rider.specialInstructions}
            </p>
          )}
        </div>
        <AttendanceStatusBadge status={rider.status} />
      </div>

      {canMark && (
        <div
          className="mt-2 grid grid-cols-3 gap-1.5"
          role="group"
          aria-label={`Mark ${rider.studentName}`}
        >
          {MARKS.map((choice) => (
            <button
              key={choice.value}
              type="button"
              aria-pressed={rider.status === choice.value}
              disabled={busy}
              onClick={() => onMark(choice.value)}
              className={`min-h-11 rounded-md border px-2 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${
                rider.status === choice.value
                  ? choice.tone
                  : 'border-line text-subtle hover:border-line-strong hover:text-muted'
              }`}
            >
              {choice.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
